import React, { memo, ReactNode, useMemo } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import type { DocumentReviewStep, ReviewFormValues } from 'src/interfaces/documentReview'
import {
  buildContractorReviewInitialValues,
  isTemporaryDriverLicense,
} from 'src/utils/documentReviewForm'
import { ContractorReviewFormContent } from './ContractorReviewFormContent'
import { buildContractorReviewValidator } from './ContractorReviewForm.validator'

interface Props {
  step: DocumentReviewStep
  /** The decision bar and context cards, rendered inside the form so they can read its values. */
  children: ReactNode
}

const ContractorReviewFormComponent = ({ step, children }: Props) => {
  const isTemporaryLicense = isTemporaryDriverLicense(step)

  const initialValues = useMemo(() => {
    return buildContractorReviewInitialValues(step)
  }, [step])

  const validationSchema = useMemo(() => {
    return buildContractorReviewValidator({
      isExpirationDateRequired: step.isExpirationDateRequired,
      isTemporaryDriverLicense: isTemporaryLicense,
    })
  }, [isTemporaryLicense, step.isExpirationDateRequired])

  return (
    <Formik<ReviewFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={() => {
        // Submission runs through the Approve / Recollect modals (Task 11), not a form submit —
        // the two actions send different bodies to different endpoints.
      }}
    >
      {/* The form and its decision bar sit in one white card, like the cards beside them. */}
      <Form
        css={tw`flex shrink-0 flex-col gap-4 rounded-lg border border-solid border-shades-100 bg-shades-0 p-6`}
      >
        <ContractorReviewFormContent
          isExpirationRequired={step.isExpirationDateRequired}
          isTemporaryDriverLicense={isTemporaryLicense}
        />
        {children}
      </Form>
    </Formik>
  )
}

export const ContractorReviewForm = memo(ContractorReviewFormComponent)
